/**
 * Utilidades para el procesamiento de productos y precios
 */

import type { ProductWithDetails } from '../types';
import type { PriceCalculationMethod } from '../stores/configStore';

/**
 * Calcula el precio final de un producto con un laboratorio específico incluyendo descuentos
 */
const calculateFinalPriceWithDiscount = (product: ProductWithDetails, laboratoryIndex: number) => {
  const lab = product.laboratories[laboratoryIndex];
  if (!lab) return 0;

  let basePrice = lab.details.PrecioDeCosto;

  // Si es un producto compuesto, sumar los precios de los componentes
  if (product.isCompuesto && product.componentes?.length) {
    basePrice = product.componentes.reduce((total, componentId) => {
      const componentProduct = product.laboratories[laboratoryIndex]?.laboratory.products?.find(
        p => p.id === componentId
      );
      return total + (componentProduct?.PrecioDeCosto || 0);
    }, 0);
  }

  const discount = lab.laboratory.descuentos?.[product.Categoria] || 0;
  
  // Aplicar descuento si existe
  const priceAfterDiscount = basePrice * (1 - discount / 100);
  
  return priceAfterDiscount * product.Factor;
};

/**
 * Calcula el precio final de un producto con un laboratorio específico sin descuentos
 */
const calculateFinalPriceWithoutDiscount = (product: ProductWithDetails, laboratoryIndex: number) => {
  const lab = product.laboratories[laboratoryIndex];
  if (!lab) return 0;

  let basePrice = lab.details.PrecioDeCosto;

  // Si es un producto compuesto, sumar los precios de los componentes
  if (product.isCompuesto && product.componentes?.length) {
    basePrice = product.componentes.reduce((total, componentId) => {
      const componentProduct = product.laboratories[laboratoryIndex]?.laboratory.products?.find(
        p => p.id === componentId
      );
      return total + (componentProduct?.PrecioDeCosto || 0);
    }, 0);
  }

  return basePrice * product.Factor;
};

/**
 * Obtiene el laboratorio con el precio base más bajo (sin descuentos)
 */
export const getLowestBasePriceLaboratory = (product: ProductWithDetails) => {
  if (!product.laboratories.length) return null;
  
  let lowestIndex = 0;
  let lowestPrice = product.laboratories[0].details.PrecioDeCosto;

  for (let i = 1; i < product.laboratories.length; i++) {
    const price = product.laboratories[i].details.PrecioDeCosto;
    if (price < lowestPrice) {
      lowestPrice = price;
      lowestIndex = i;
    }
  }

  return product.laboratories[lowestIndex];
};

/**
 * Obtiene el laboratorio con el precio final más bajo (con descuentos)
 */
export const getLowestFinalPriceLaboratory = (product: ProductWithDetails) => {
  if (!product.laboratories.length) return null;
  
  let lowestIndex = 0;
  let lowestPrice = calculateFinalPriceWithDiscount(product, 0);

  for (let i = 1; i < product.laboratories.length; i++) {
    const price = calculateFinalPriceWithDiscount(product, i);
    if (price < lowestPrice) {
      lowestPrice = price;
      lowestIndex = i;
    }
  }

  return product.laboratories[lowestIndex];
};

/**
 * Obtiene el laboratorio con el precio final más alto
 */
export const getHighestPriceLaboratory = (product: ProductWithDetails) => {
  if (!product.laboratories.length) return null;
  
  let highestIndex = 0;
  let highestPrice = calculateFinalPriceWithDiscount(product, 0);

  for (let i = 1; i < product.laboratories.length; i++) {
    const price = calculateFinalPriceWithDiscount(product, i);
    if (price > highestPrice) {
      highestPrice = price;
      highestIndex = i;
    }
  }

  return product.laboratories[highestIndex];
};

/**
 * Calcula el precio promedio de los laboratorios de un producto
 */
export const getAveragePriceLaboratory = (product: ProductWithDetails) => {
  if (!product.laboratories.length) return null;
  
  const prices = product.laboratories.map((_, index) => 
    calculateFinalPriceWithDiscount(product, index)
  );
  
  const avgPrice = prices.reduce((sum, price) => sum + price, 0) / prices.length;
  
  let closestIndex = 0;
  let closestDiff = Math.abs(prices[0] - avgPrice);

  for (let i = 1; i < prices.length; i++) {
    const diff = Math.abs(prices[i] - avgPrice);
    if (diff < closestDiff) {
      closestDiff = diff;
      closestIndex = i;
    }
  }

  return product.laboratories[closestIndex];
};

/**
 * Obtiene el laboratorio según el método de cálculo especificado
 */
export const getLaboratoryByMethod = (
  product: ProductWithDetails, 
  method: PriceCalculationMethod
) => {
  switch (method) {
    case 'lowest':
      return getLowestBasePriceLaboratory(product);
    case 'highest':
      return getHighestPriceLaboratory(product);
    case 'average':
      return getAveragePriceLaboratory(product);
    default:
      return getLowestBasePriceLaboratory(product);
  }
};

/**
 * Procesa un producto según la configuración de visualización
 */
export const processProduct = (
  product: ProductWithDetails,
  displayMode: 'single' | 'multiple',
  priceMethod: PriceCalculationMethod
): ProductWithDetails[] => {
  if (displayMode === 'multiple') {
    // Modo múltiple: crear una entrada por cada laboratorio
    return product.laboratories.map(lab => ({
      ...product,
      laboratories: [lab]
    }));
  }

  // Modo único: mostrar el producto con el laboratorio seleccionado según el método
  // Para el precio de venta usamos el laboratorio con menor precio base
  const labForPrice = getLaboratoryByMethod(product, priceMethod);
  // Para mostrar en la columna de laboratorio usamos el de menor precio final con descuentos
  const labToShow = getLowestFinalPriceLaboratory(product);

  if (!labForPrice || !labToShow) return [product];

  return [{
    ...product,
    laboratories: [{
      laboratory: labToShow.laboratory,
      details: {
        ...labForPrice.details,
        // Mantenemos el precio de costo del laboratorio con menor precio base
        PrecioDeCosto: labForPrice.details.PrecioDeCosto
      }
    }]
  }];
};

/**
 * Procesa una lista de productos según su configuración
 */
export const processProducts = (
  products: ProductWithDetails[],
  getCategoryConfig: (categoryId: string) => { 
    displayMode: 'single' | 'multiple';
    priceCalculationMethod: PriceCalculationMethod;
  }
): ProductWithDetails[] => {
  return products.flatMap(product => {
    const config = getCategoryConfig(product.Categoria);
    return processProduct(product, config.displayMode, config.priceCalculationMethod);
  });
};

/**
 * Verifica si hay ciclos en la composición de productos
 */
export const checkForCycles = (
  productId: string,
  componentes: string[],
  allProducts: ProductWithDetails[]
): boolean => {
  const visited = new Set<string>();
  
  const hasCycle = (currentId: string): boolean => {
    if (visited.has(currentId)) return true;
    visited.add(currentId);
    
    const product = allProducts.find(p => p.id === currentId);
    if (!product?.isCompuesto || !product.componentes?.length) {
      visited.delete(currentId);
      return false;
    }
    
    for (const componentId of product.componentes) {
      if (hasCycle(componentId)) return true;
    }
    
    visited.delete(currentId);
    return false;
  };
  
  // Verificar ciclos para cada componente nuevo
  for (const componentId of componentes) {
    if (componentId === productId) return true; // Auto-referencia
    if (hasCycle(componentId)) return true;
  }
  
  return false;
};

/**
 * Verifica si todos los componentes son del mismo laboratorio
 */
export const validateSameLaboratory = (
  laboratoryId: string,
  componentes: string[],
  allProducts: ProductWithDetails[]
): boolean => {
  return componentes.every(componentId => {
    const product = allProducts.find(p => p.id === componentId);
    return product?.laboratories.some(lab => lab.laboratory.id === laboratoryId);
  });
};

export {
  calculateFinalPriceWithDiscount,
  calculateFinalPriceWithoutDiscount
};